import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Shield, Check, X, MessageSquare, List, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";

interface AdminReview {
  id: string;
  content_id: string;
  content_type: string;
  status: string;
  report_count: number;
  created_at: string;
  posts: {
    content: string;
    user_id: string;
    profiles: {
      username: string;
    };
  };
}

export const AdminReviewDashboard = () => {
  const [reviews, setReviews] = useState<AdminReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingReviews, setProcessingReviews] = useState<{ [key: string]: boolean }>({});
  const [reviewNotes, setReviewNotes] = useState<{ [key: string]: string }>({});
  const { toast } = useToast();

  useEffect(() => {
    fetchReviews();
    subscribeToNewReviews();
  }, []);

  const subscribeToNewReviews = () => {
    const channel = supabase
      .channel('admin-reviews')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'admin_reviews',
        },
        (payload) => {
          fetchReviews();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const fetchReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_reviews')
        .select(`
          *,
          posts!inner (
            content,
            user_id
          )
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch user profiles separately to avoid relationship issues
      const enrichedReviews = await Promise.all(
        (data || []).map(async (review) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('username')
            .eq('id', review.posts.user_id)
            .maybeSingle();

          return {
            ...review,
            posts: {
              ...review.posts,
              profiles: {
                username: profile?.username || 'Unknown User'
              }
            }
          };
        })
      );

      setReviews(enrichedReviews as any);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast({
        title: "Error fetching reviews",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createNotification = async (userId: string, content: string, type: 'like' | 'comment' | 'mention') => {
    try {
      const { error } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          type,
          content,
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  const handleReview = async (reviewId: string, status: 'approved' | 'rejected') => {
    // Set processing state to disable buttons
    setProcessingReviews(prev => ({ ...prev, [reviewId]: true }));
    
    try {
      const review = reviews.find(r => r.id === reviewId);
      if (!review) return;

      const { error } = await supabase
        .from('admin_reviews')
        .update({
          status,
          review_notes: reviewNotes[reviewId] || '',
          reviewed_at: new Date().toISOString(),
          reviewer_id: (await supabase.auth.getUser()).data.user?.id,
          action_taken: status === 'approved' ? 'content_restored' : 'content_removed'
        })
        .eq('id', reviewId);

      if (error) throw error;

      // If rejected, hide the post
      if (status === 'rejected') {
        await supabase
          .from('posts')
          .update({ is_private: true })
          .eq('id', review.content_id);
      }

      // Create notification for the user
      const notificationContent = status === 'approved' 
        ? "Your reported content has been reviewed and approved."
        : `Your content has been removed for violating our guidelines. Reason: ${reviewNotes[reviewId] || 'No reason provided'}`;

      await createNotification(
        review.posts.user_id,
        notificationContent,
        'mention'
      );

      toast({
        title: `Content ${status}`,
        description: `The reported content has been ${status}`,
      });

      // Remove the reviewed item from the list
      setReviews(reviews.filter(r => r.id !== reviewId));
      
    } catch (error) {
      console.error('Error updating review:', error);
      toast({
        title: "Error updating review",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      // Clear processing state
      setProcessingReviews(prev => ({ ...prev, [reviewId]: false }));
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="flex items-center gap-2 mb-6">
        <Shield className="w-6 h-6 text-red-500" />
        <h1 className="text-2xl font-bold">Content Review Dashboard</h1>
      </div>

      <div className="space-y-4">
        {reviews.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <p className="text-center text-muted-foreground">
                No content currently needs review
              </p>
            </CardContent>
          </Card>
        ) : (
          reviews.map((review) => (
            <Card key={review.id} className="relative">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <List className="w-4 h-4" />
                      Reported Content
                    </CardTitle>
                    <CardDescription>
                      Posted by {review.posts.profiles.username} • 
                      Reported {review.report_count} times
                    </CardDescription>
                  </div>
                  <Badge variant="destructive">
                    Pending Review
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ScrollArea className="h-[100px] w-full rounded-md border p-4">
                  {review.posts.content}
                </ScrollArea>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    <MessageSquare className="w-4 h-4 inline mr-2" />
                    Review Notes
                  </label>
                  <Textarea
                    placeholder="Add notes about your decision..."
                    value={reviewNotes[review.id] || ''}
                    onChange={(e) => 
                      setReviewNotes({
                        ...reviewNotes,
                        [review.id]: e.target.value
                      })
                    }
                    className="min-h-[100px]"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => handleReview(review.id, 'rejected')}
                    disabled={processingReviews[review.id]}
                  >
                    {processingReviews[review.id] ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <X className="w-4 h-4 mr-2" />
                    )}
                    Reject & Remove
                  </Button>
                  <Button
                    variant="outline"
                    className="text-green-500 hover:text-green-600 hover:bg-green-50"
                    onClick={() => handleReview(review.id, 'approved')}
                    disabled={processingReviews[review.id]}
                  >
                    {processingReviews[review.id] ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Approve
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};